import {inject, Injectable} from '@angular/core';
import {Actions, concatLatestFrom, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {catchError, distinctUntilChanged, filter, map, of, switchMap, take, tap} from 'rxjs';
import {DatevWizardLoginActions} from './datev-wizard-login.actions';
import {ApproveExportConfigRequestDTO, DatevIncometaxExportConfigService} from '../../../../../openapi/export-openapi';
import {MatSnackBar} from '@angular/material/snack-bar';
import {NGXLogger} from 'ngx-logger';
import {DatevWizardGetFirmaActions} from '../../datev-wizard-get-firma/+store/datev-wizard-get-firma.actions';
import {DatevWizardClientIdActions} from '../../datev-wizard-client-id/+store/datev-wizard-client-id.actions';
import {mappedHttpErrorResponseOperator, RouterActions, RouterSelectors} from '@adnova/jf-ng-components';
import {DatevWizardClientIdSelectors} from '../../datev-wizard-client-id/+store/datev-wizard-client-id.selectors';
import {DatevWizardRetryContextService} from '../services/datev-wizard-retry-context.service';
import {DEFAULT_OAUTH_ERROR_DESCRIPTION} from '../../+store/datev-wizard.constants';


@Injectable()
export class DatevWizardLoginEffects {

  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _exportConfigService = inject(DatevIncometaxExportConfigService);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _logger = inject(NGXLogger);
  private readonly _retryContextService = inject(DatevWizardRetryContextService);

  /**
   * Verarbeitet den DATEV-Login-Callback
   * Ruft das Backend auf um den DATEV Authorization Code gegen Access Token zu tauschen
   * 
   * WORKAROUND Teil 2/2: Verarbeitung nach Umleitung
   * Diese Action wird dispatched, nachdem die DATEV-Login-Parameter erfolgreich in Route-Parameter
   * umgewandelt wurden. Zu diesem Zeitpunkt sind die Query-Parameter aus der URL entfernt
   * und der globale OAuth-Handler (für die App-Authentifizierung am JustFarming-Portal) 
   * kann nicht mehr interferieren.
   * 
   * Die DATEV-Parameter kommen jetzt aus der Route statt aus Query-Parametern, was die
   * Isolation vom OAuth-System der Anwendung sicherstellt.
   */
  readonly login$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardLoginActions.login),
      switchMap(({code, state, sessionState}) => {
        const approveExportConfigRequestDTO: ApproveExportConfigRequestDTO = {
          code,
          state,
          sessionState,
        };

        return this._exportConfigService.approveExportConfigRequest(approveExportConfigRequestDTO).pipe(
          map(response => DatevWizardLoginActions.loginSuccess({response})),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            switchMap(error => {
              return of(DatevWizardLoginActions.loginFailure({error}));
            })
          ))
        );
      })
    )
  );

  /**
   * Nach erfolgreichem OAuth-Callback zur config-details navigieren
   * Die Response enthält die exportConfigId, die direkt für die Navigation verwendet wird
   */
  readonly loginSuccess$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardLoginActions.loginSuccess),
      tap(() => {
        // Auto-Cleanup: Lösche gespeicherte Retry-URL nach erfolgreichem Login
        this._retryContextService.clearRetryUrl();
      }),
      map(({response}) => {
        // Response enthält die exportConfigId
        const exportConfigId = response.id;

        if (!exportConfigId) {
          // Fallback: Wenn keine exportConfigId gefunden, zurück zum Start
          this._logger.error('exportConfigId nicht in Response gefunden');
          this._snackBar.open(
            'Fehler: Konfiguration unvollständig',
            'OK',
            {
              duration: 5000,
              panelClass: 'error'
            }
          );
          return DatevWizardLoginActions.goToWizardStart();
        }

        // Navigiere direkt zur config-details mit exportConfigId
        return DatevWizardLoginActions.navigateToConfigDetails({
          exportConfigId,
        });
      })
    )
  );

  /**
   * Navigation zur config-details Seite mit exportConfigId
   */
  readonly navigateToConfigDetails$ = createEffect(() =>
      this._actions$.pipe(
        ofType(DatevWizardLoginActions.navigateToConfigDetails),
        map(({exportConfigId}) => {
          const url = `/datev/income-tax-documents/wizard/config-details/export-config-id/${exportConfigId}`;
          return RouterActions.navigateByUrl({url});
        })
      ),
  );

  /**
   * Bei Fehler: Zeige Fehlermeldung und dispatche Navigation-Action
   */
  readonly loginFailure$ = createEffect(() =>
      this._actions$.pipe(
        ofType(DatevWizardLoginActions.loginFailure),
        tap(({error}) => {
          this._snackBar.open(
            'DATEV-Verbindung fehlgeschlagen. Bitte versuchen Sie es erneut.',
            'OK',
            {
              duration: 5000,
              panelClass: 'error'
            }
          );
        }),
        map(() => DatevWizardLoginActions.goToWizardStart())
      )
  );

  /**
   * Initialisiert den Login-Prozess
   * Prüft Route- und Query-Parameter und entscheidet über nächste Action
   * 
   * WORKAROUND: Diese Implementierung löst das Problem mit konkurrierenden Authentifizierungs-Flows:
   * - Die Anwendung nutzt OAuth2 für die Authentifizierung am JustFarming-Portal (globaler OAuth-Handler)
   * - DATEV nutzt ebenfalls OAuth2 für seinen eigenen Login-Prozess
   * - Problem: Beide verwenden dieselben Parameter-Namen ('code', 'state'), was zu Konflikten führt
   * - Der globale OAuth-Handler der App würde die DATEV-Login-Parameter abfangen und fehlinterpretieren
   * - Lösung: Bei DATEV-Query-Parametern leiten wir sofort zur parametrisierten Route um,
   *   wodurch die Query-Parameter aus der URL entfernt werden und der App-OAuth-Handler 
   *   sie nicht mehr sieht
   * 
   * Ablauf:
   * 1. DATEV-Login leitet zurück mit Query-Parametern (?code=X&state=Y&session_state=Z)
   * 2. Dieser Effect erkennt die DATEV-Query-Parameter
   * 3. Navigation zur parametrisierten Route (/login/code/X/state/Y/session-state/Z)
   * 4. Komponente wird neu geladen, Effect erkennt jetzt Route-Parameter
   * 5. DATEV-Login-Prozess wird mit den Route-Parametern gestartet
   */
  readonly initializeLogin$ = createEffect(() =>
    this._actions$.pipe(
      ofType(DatevWizardLoginActions.initializeLogin),
      concatLatestFrom(() => [
        // Route-Parameter über Store-Selektoren abrufen (Success-Flow)
        this._store.select(RouterSelectors.paramByKey('code')),
        this._store.select(RouterSelectors.paramByKey('state')),
        this._store.select(RouterSelectors.paramByKey('sessionState')),
        // Query-Parameter über Store-Selektoren abrufen (Success-Flow)
        this._store.select(RouterSelectors.queryParamByKey('code')),
        this._store.select(RouterSelectors.queryParamByKey('state')),
        this._store.select(RouterSelectors.queryParamByKey('session_state')),
        // Route-Parameter für Error-Flow
        this._store.select(RouterSelectors.paramByKey('errorCode')),
        this._store.select(RouterSelectors.paramByKey('errorDescription')),
        // Query-Parameter für Error-Flow
        this._store.select(RouterSelectors.queryParamByKey('error')),
        this._store.select(RouterSelectors.queryParamByKey('error_description'))
      ]),
      map(([,
        routeCode, routeStateSuccess, routeSessionState,
        queryCode, queryStateSuccess, querySessionState,
        routeErrorCode, routeErrorDescription,
        queryError, queryErrorDescription
      ]) => {
        // Route-Parameter vorhanden - Login starten
        if (routeCode && routeStateSuccess && routeSessionState) {
          return DatevWizardLoginActions.login({
            code: routeCode,
            state: routeStateSuccess,
            sessionState: routeSessionState,
          });
        }

        // Query-Parameter vorhanden - zur parametrisierten Route umleiten
        if (queryCode && queryStateSuccess && querySessionState) {
          return DatevWizardLoginActions.goToParameterizedRoute({
            code: queryCode,
            state: queryStateSuccess,
            sessionState: querySessionState,
          });
        }

        // Error-Route-Parameter vorhanden - OAuth-Fehler verarbeiten
        if (routeErrorCode) {
          // state Parameter ist auch ein Route-Parameter in der Error-Route
          return DatevWizardLoginActions.oauthError({
            errorCode: routeErrorCode,
            errorDescription: routeErrorDescription || DEFAULT_OAUTH_ERROR_DESCRIPTION,
            state: routeStateSuccess || 'unknown'
          });
        }

        // Error-Query-Parameter vorhanden - zur parametrisierten Error-Route umleiten
        if (queryError) {
          // state Parameter ist auch ein Query-Parameter im Error-Flow
          return DatevWizardLoginActions.goToParameterizedErrorRoute({
            errorCode: queryError,
            errorDescription: queryErrorDescription || DEFAULT_OAUTH_ERROR_DESCRIPTION,
            state: queryStateSuccess || 'unknown'
          });
        }

        // Weder Route- noch Query-Parameter vorhanden - Fehler
        return DatevWizardLoginActions.loginFailure({
          error: { message: 'Fehlende Parameter' }
        });
      })
    )
  );

  /**
   * Navigiert zur parametrisierten Route (entfernt Query-Parameter)
   * 
   * WORKAROUND Teil 1/2: Umleitung von Query- zu Route-Parametern
   * Diese Action wird dispatched, wenn DATEV-Login-Parameter als Query-Parameter ankommen.
   * Die Navigation zur parametrisierten Route entfernt die Query-Parameter aus der URL,
   * sodass der OAuth-Handler der Anwendung (für JustFarming-Portal-Authentifizierung) 
   * diese nicht als eigene Authentifizierungs-Parameter interpretiert.
   * 
   * WICHTIG: URL-Encoding der Parameter
   * Die OAuth-Parameter (besonders session_state) können Sonderzeichen enthalten, die in
   * URL-Pfaden problematisch sind (z.B. "/" oder "%"). Daher werden alle Parameter
   * URL-encoded bevor sie in die Route eingebaut werden. Der Angular Router decoded
   * diese automatisch beim Auslesen über RouterSelectors, sodass das Backend die
   * Original-Werte erhält.
   * 
   * Beispiel problematischer session_state:
   * "UTb_JaoM2t.4Xs/Ei3IkgF" würde ohne Encoding die Route brechen, da "/" als
   * Pfad-Separator interpretiert wird.
   * 
   * Wichtig: Sowohl DATEV als auch die App nutzen OAuth2, aber für unterschiedliche Zwecke:
   * - App: OAuth2 für Benutzer-Authentifizierung am JustFarming-Portal
   * - DATEV: OAuth2 für Autorisierung des Datenzugriffs auf DATEV-Systeme
   * 
   * Beispiel-Transformation:
   * Von: /datev/income-tax-documents/wizard/login?code=abc&state=xyz&session_state=123/456 (DATEV-Parameter)
   * Nach: /datev/income-tax-documents/wizard/login/code/abc/state/xyz/session-state/123%2F456 (isoliert vom App-OAuth)
   */
  readonly goToParameterizedRoute$ = createEffect(() =>
    this._actions$.pipe(
      ofType(DatevWizardLoginActions.goToParameterizedRoute),
      map(({code, state, sessionState}) => {
        // URL-encode alle Parameter für sichere Verwendung in der Route
        const encodedCode = encodeURIComponent(code);
        const encodedState = encodeURIComponent(state);
        const encodedSessionState = encodeURIComponent(sessionState);

        // Navigation zur parametrisierten Route mit encoded Werten
        const url = `/datev/income-tax-documents/wizard/login/code/${encodedCode}/state/${encodedState}/session-state/${encodedSessionState}`;
        return RouterActions.navigateByUrl({url});
      })
    )
  );

  /**
   * Navigiert zur parametrisierten Error-Route (entfernt Query-Parameter)
   *
   * WORKAROUND: Analog zum Success-Flow werden auch Error-Parameter von Query zu Route konvertiert.
   * Wenn der DATEV OAuth-Flow mit einem Fehler abbricht (z.B. User lehnt Zugriff ab),
   * redirected DATEV zurück mit error und error_description als Query-Parameter.
   *
   * Diese Navigation entfernt die Query-Parameter, um Konflikte mit dem App-OAuth-Handler zu vermeiden.
   *
   * Beispiel-Transformation:
   * Von: /wizard/login?error=access_denied&error_description=Resource%20owner%20did%20not%20approve&state=xyz
   * Nach: /wizard/login/error/access_denied/error-description/Resource%20owner%20did%20not%20approve/state/xyz
   */
  readonly goToParameterizedErrorRoute$ = createEffect(() =>
    this._actions$.pipe(
      ofType(DatevWizardLoginActions.goToParameterizedErrorRoute),
      map(({errorCode, errorDescription, state}) => {
        // URL-encode alle Parameter für sichere Verwendung in der Route
        const encodedErrorCode = encodeURIComponent(errorCode);
        const encodedErrorDescription = encodeURIComponent(errorDescription);
        const encodedState = encodeURIComponent(state);

        // Navigation zur parametrisierten Error-Route mit encoded Werten
        const url = `/datev/income-tax-documents/wizard/login/error/${encodedErrorCode}/error-description/${encodedErrorDescription}/state/${encodedState}`;
        return RouterActions.navigateByUrl({url});
      })
    )
  );

  /**
   * Navigiert zum Wizard-Start
   */
  readonly goToWizardStart$ = createEffect(() =>
    this._actions$.pipe(
      ofType(DatevWizardLoginActions.goToWizardStart),
      map(() => RouterActions.navigateByUrl({url: '/datev/wizard'}))
    )
  );

  /**
   * Retry: Navigiert zurück zur gespeicherten URL
   * Wird vom Retry-Button im Error-UI aufgerufen
   *
   * Spezialfall: Wenn die URL von export-date kommt, wird zu client-id navigiert,
   * da nach einem Fehler die Client-ID-Auswahl der richtige Einstiegspunkt ist.
   */
  readonly retry$ = createEffect(() =>
    this._actions$.pipe(
      ofType(DatevWizardLoginActions.retry),
      map(() => {
        const retryUrl = this._retryContextService.getRetryUrl();

        if (!retryUrl) {
          // Fallback: Wenn keine URL gespeichert, zurück zum Wizard-Start (get-firma)
          this._logger.warn('Keine Retry-URL gefunden, navigiere zum Wizard-Start');
          return RouterActions.navigateByUrl({url: '/datev/income-tax-documents/wizard/get-firma'});
        }

        // Parse URL und extrahiere nur den Pfad (Router benötigt relativen Pfad, keine vollständige URL)
        try {
          const url = new URL(retryUrl);
          let pathname = url.pathname;

          // Wenn von export-date: Umleitung zu client-id
          // export-date URL: /wizard/export-date/inhaber/{firmenId}/fall/{fall}/client/{clientId}
          // client-id URL:   /wizard/client-id/inhaber/{firmenId}/fall/{fall}
          if (pathname.includes('/export-date/')) {
            pathname = pathname
              .replace('/export-date/', '/client-id/')
              .replace(/\/client\/[^/]+$/, ''); // Entfernt /client/{clientId} am Ende
          }

          return RouterActions.navigateByUrl({url: pathname});
        } catch (error) {
          // Fallback: Falls URL-Parsing fehlschlägt, versuche direkt zu navigieren
          this._logger.error('Fehler beim Parsen der Retry-URL', error);
          return RouterActions.navigateByUrl({url: '/datev/income-tax-documents/wizard/get-firma'});
        }
      })
    )
  );
}
